// eslint-disable-next-line no-use-before-define
import React, { useState, useEffect, useRef, FormEvent, Fragment } from 'react';
import { gql, useQuery, useMutation, useApolloClient } from '@apollo/client';
import { Box, styled } from '@mui/system';

import { useHistory, useLocation, Redirect } from 'react-router-dom';
import 'url-search-params-polyfill';
import * as Sentry from '@sentry/browser';

import './UKPatientSearch.scss';

import { PatientOptIn } from 'op-interfaces';
import { ReactListData } from 'shared-components/interfaces';
import { DateTimeConverter, Logger } from 'shared-components/utils';

import { PageContainer } from 'op-components';
import { LoadingSpinner } from 'shared-components/components';
import { DropDownField } from 'shared-components/components/FormFields';

import { BulletPoint, Success } from 'shared-components/images';
import { ErrorOutline as ErrorOutlineIcon } from '@mui/icons-material';
import { Typography, Stack, Button, Divider } from '@mui/material';
import { LoadingButton } from '@mui/lab';

const logger = new Logger('PatientSearch');

const BASE_SEARCH_URL = '/search';
const SEARCH_NAME = 'q';
const FILTER = 'filter';
const FILTER_PLACEHOLDER = 'Select filter';

const UK_PATIENT_SEARCH = gql`
  query ukPatientSearch($searchTerm: String, $submissionStatus: String, $primaryCenterId: Int, $page: Int) {
    searchPatients: ukPatientSearch(
      search: $searchTerm
      threshold: 0.5
      submissionStatus: $submissionStatus
      primaryCenterId: $primaryCenterId
      page: $page
    ) {
      id
      firstName
      lastName
      ida
      dob
      primaryPhone
      primaryPhoneFormatted
      email
      pxOptedIn
      regFormStatus
      pxOptOutComment
      address {
        id
        line1
        line2
        city
        state
        country
        postcode
      }
    }
  }
`;
export const SEARCH_FILTERS_REF_DATA_QUERY = gql`
  query ukSearchFilters {
    searchFiltersRefData: listData(category: "ukSearchFilters") {
      id
      name
      appKey
    }
  }
`;

export const GET_USER_DETAILS = gql`
  query UserDetails {
    user {
      id
      username
      features {
        distressThermometer
      }
    }
  }
`;

export const CREATE_PATIENT = gql`
  mutation CreatePatient {
    createPatient {
      patient {
        id
      }
    }
  }
`;

export interface PatientSearchQueryItemAddress {
  line1: string;
  line2: string;
  city: string;
  state: string;
  country: string;
  postcode: string;
}

export interface PatientSearchQueryItem {
  id: string;
  firstName: string;
  lastName: string;
  ida: string;
  dob: string;
  primaryPhone: string;
  primaryPhoneFormatted: string;
  email: string;
  address: PatientSearchQueryItemAddress;
  pxOptedIn: boolean | null;
  pxOptOutComment: string;
  regFormStatus: string;
  hasDraftCareplan?: boolean;
}

export interface PatientSearchQueryResultData {
  searchPatients: PatientSearchQueryItem[];
  user?: {
    features: {
      DT: boolean;
    };
  };
}

export interface PatientSearchQueryResultHandler {
  loading: boolean;
  error: string[];
  data: PatientSearchQueryResultData;
}

interface SearchFiltersQueryResultData {
  searchFiltersRefData: ReactListData[];
}

interface UserDetailsResponse {
  data: {
    user: {
      id: string;
      username: string;
      features: {
        distressThermometer: boolean;
      };
    };
  };
}

const StyledSearchInput = styled('input')`
  max-width: 872px;
  flex: 1;
  font-size: 16px;
  line-height: 0.66rem;
  padding: 12px 24px 12px 48px;
  background-image: url('../../../../../shared_components/images/Search.svg');
  background-repeat: no-repeat;
  background-size: 24px 24px;
  background-position: 12px 12px;
  background-color: ${(props) => props.theme.palette.grey[100]};
  border: 2px solid ${(props) => props.theme.palette.grey[600]};
  margin-right: 16px;

  &:focus {
    background-image: url('../../../../../shared_components/images/Search_Green.svg');
    background-repeat: no-repeat;
    background-size: 24px 24px;
    background-position: 12px 12px;
    outline: none;
  }
`;

const UKPatientSearch = (): JSX.Element => {
  const history = useHistory();
  const location = useLocation();
  const client = useApolloClient();

  const [distressThermometerEnabled, setDistressThermometerEnabled] = useState(false);

  const liveSearchInputRef = useRef<HTMLInputElement | null>(null);
  const triggerSubmitRef = useRef<HTMLFormElement | null>(null);
  const submissionStatusRef = useRef<string>('');

  // Extract search term from URL
  const extractSearchTerm = (): string | undefined => {
    const searchParams = location.search;
    if (searchParams) {
      const urlSearchParams = new URLSearchParams(searchParams);
      const query = urlSearchParams.get(SEARCH_NAME);
      return query || undefined;
    }
    return undefined;
  };

  // Extract filter from URL
  const extractFilter = (): string => {
    const searchParams = location.search;
    if (searchParams) {
      const urlSearchParams = new URLSearchParams(searchParams);
      const query = urlSearchParams.get(FILTER);
      return query || '';
    }
    return '';
  };

  const searchTerm = extractSearchTerm();
  const submissionStatus = extractFilter();
  submissionStatusRef.current = submissionStatus;

  // Fetch search filters
  const { loading: filtersLoading, data: filtersData } =
    useQuery<SearchFiltersQueryResultData>(SEARCH_FILTERS_REF_DATA_QUERY);

  // Fetch search results
  const {
    loading: searchLoading,
    error: searchError,
    data: searchData,
  } = useQuery<PatientSearchQueryResultData>(UK_PATIENT_SEARCH, {
    variables: { searchTerm, submissionStatus },
    skip: !searchTerm,
  });

  // Create patient mutation
  const [createPatient, { loading: createLoading, data: createData }] = useMutation<{
    createPatient: { patient: { id: string } };
  }>(CREATE_PATIENT);

  useEffect(() => {
    if (!client) return;

    client.query({ query: GET_USER_DETAILS }).then((results: UserDetailsResponse): void => {
      {
        const scope = Sentry.getCurrentScope();
        scope.setTag('user_type', 'pso');
        scope.setUser({
          id: results.data.user.id,
          username: results.data.user.username,
        });
      }

      setDistressThermometerEnabled(results.data.user.features.distressThermometer);
    });

    // If we've landed on search page. Reset any cache for viewed pages.
    client.writeQuery({
      query: gql`
        query {
          registrationPagesViewed
        }
      `,
      data: {
        registrationPagesViewed: [],
      },
    });
  }, [client]);

  const submitHandler = (event: FormEvent<HTMLFormElement>): void => {
    if (liveSearchInputRef.current && liveSearchInputRef.current.value === '') {
      event.preventDefault();
    }
  };

  const renderContentDetails = (title: string, value?: string | JSX.Element): JSX.Element => {
    let displayedValue = value;
    if (displayedValue === undefined || displayedValue === null || displayedValue === '') {
      displayedValue = 'Not provided';
    }

    return (
      <Stack direction="row" gap={1}>
        <Typography variant="body1" fontWeight={600}>
          {title}&#58;
        </Typography>
        <Typography variant="body1">{displayedValue}</Typography>
      </Stack>
    );
  };

  const renderLine2 = (line2?: string): JSX.Element => {
    if (line2) {
      return (
        <Fragment>
          {line2}&#44;
          <br />
        </Fragment>
      );
    }

    return <Fragment />;
  };

  const createAddress = (address?: PatientSearchQueryItemAddress): JSX.Element | undefined => {
    if (address) {
      const { line1, line2, city, state, country, postcode } = address;

      if (line1 || city || state || country || postcode) {
        return (
          <Fragment>
            <div>
              {line1}&#44;
              <br />
              {renderLine2(line2)}
              {city}&#44;
              <br />
              {state} {country} {postcode}
            </div>
          </Fragment>
        );
      }
    }

    return undefined;
  };

  const renderFormStatus = (regFormStatus: string): JSX.Element => {
    let icon = <div />; // FIXME: Temporary placeholder
    let message = '';

    if (regFormStatus === 'filterSubmittedToMosaiq') {
      icon = <Success className="reviewed icon" />;
      message = 'Submitted to Mosaiq';
    } else if (regFormStatus === 'filterReviewRequired') {
      icon = <ErrorOutlineIcon color="warning" className="icon" />;
      message = 'Review required';
    }

    return (
      <div className="patient-search-form-status-container">
        {icon}
        <span className="patient-search-form-status">{message}</span>
      </div>
    );
  };

  const renderSearchResults = (data: PatientSearchQueryResultData): JSX.Element[] | undefined => {
    if (data && data.searchPatients && data.searchPatients.length > 0) {
      const searchResults = data.searchPatients.map((patient: PatientSearchQueryItem): JSX.Element => {
        return (
          <>
            <Stack
              direction="column"
              key={patient.ida}
              id={`patient_${patient.ida}`}
              gap={2}
              sx={{ padding: '8px 0px' }}>
              <Typography variant="h5" fontWeight="bold" data-testid="patient-search-name">
                {patient.firstName} {patient.lastName}
              </Typography>
              <Stack direction="row" gap="72px">
                <Box width={320}>
                  {renderContentDetails('Patient ID', patient.ida)}
                  {renderContentDetails('Date of birth', DateTimeConverter.getFormattedDateAsDDMonthYYYY(patient.dob))}
                  {renderContentDetails('Phone', patient.primaryPhone)}
                </Box>
                <Box width={320}>{renderContentDetails('Address', createAddress(patient.address))}</Box>
                <Stack direction="row" gap={2}>
                  <Button
                    variant="outlined"
                    sx={{ height: '42px' }}
                    onClick={(): void => {
                      history.push(`/registration/${patient.id}/basic`);
                    }}>
                    Registration form
                  </Button>
                  {renderFormStatus(patient.regFormStatus)}
                </Stack>
              </Stack>
            </Stack>
            <Divider />
          </>
        );
      });

      return searchResults;
    }

    return;
  };

  const renderNoSearchResults = (): JSX.Element => {
    return (
      <Typography
        style={{
          margin: '48px 0px 48px 8px',
          fontWeight: 'bold',
        }}
        variant="h6"
        id="patient-search-no-results">
        Sorry we couldn't find any results matching &nbsp;&quot;{searchTerm}&quot;
      </Typography>
    );
  };

  const renderSearchTips = (resultsFound: boolean): JSX.Element => {
    let message = "Can't find what you're looking for? Try some of these search tips";
    if (!resultsFound) {
      message = 'Search tips';
    }

    return (
      <div id="patient-search-tips">
        <div id="patient-search-tips-heading">{message}</div>
        <div id="patient-search-tips-points">
          <ul>
            <li>
              <BulletPoint />
              Check your spelling and try again
            </li>
            <li>
              <BulletPoint />
              Just created a new patient in the EMR? Please wait one minute and then refresh the page
            </li>
            <li>
              <BulletPoint />
              Search using the patients' first name and last name or patient IDA
            </li>
            <li>
              <BulletPoint />
              Try to clear the status filter
            </li>
          </ul>
        </div>
      </div>
    );
  };

  const renderContents = (data: PatientSearchQueryResultData): JSX.Element | JSX.Element[] | undefined => {
    if (searchTerm) {
      if (data && data.searchPatients && data.searchPatients.length > 0) {
        return renderSearchResults(data);
      } else {
        return renderNoSearchResults();
      }
    }

    return;
  };

  const renderMakePatientButton = (): JSX.Element => {
    if (!createLoading && createData) {
      const { id } = createData.createPatient.patient;
      return <Redirect to={`/registration/${id}/basic`} push />;
    }
    const text = createLoading ? 'Loading' : 'Register new patient';
    return (
      <LoadingButton
        sx={{ marginLeft: 2 }}
        variant="outlined"
        size="large"
        type="submit"
        data-testid="uk-create-patient"
        onClick={(): void => {
          createPatient();
        }}
        loading={createLoading}>
        {text}
      </LoadingButton>
    );
  };

  const renderSearchResultsSection = (): JSX.Element => {
    if (searchLoading) {
      return <LoadingSpinner />;
    }

    if (searchError || !searchData) {
      return renderNoSearchResults();
    }

    return (
      <Box id="patient-search-results">
        {renderContents(searchData)}
        {searchTerm !== undefined &&
          renderSearchTips(searchData && searchData.searchPatients && searchData.searchPatients.length > 0)}
      </Box>
    );
  };

  logger.debug('render', 'distressThermometerEnabled: ', distressThermometerEnabled);

  if (filtersLoading) return <LoadingSpinner />;

  return (
    <Fragment>
      <PageContainer>
        <Stack
          sx={{
            padding: '32px',
            position: 'sticky',
            top: 0,
            backgroundColor: 'background.paper',
            zIndex: 10,
            minHeight: '225px',
          }}>
          <div id="patient-search-title">{'Patient Search'}</div>
          <form
            action="/search"
            id="patient-search-form"
            method="GET"
            onSubmit={submitHandler}
            ref={(ref): void => {
              triggerSubmitRef.current = ref;
            }}>
            <div className="patient-search-input-container">
              <StyledSearchInput
                type="text"
                id="patient-search-input"
                data-testid="patient-search-input"
                name={SEARCH_NAME}
                placeholder={'Search patient name or enter GC number'}
                defaultValue={searchTerm}
                ref={(ref): void => {
                  liveSearchInputRef.current = ref;
                }}
              />
              <Button data-testid="patient-search" size="large" type="submit" variant="contained">
                Search
              </Button>
              {renderMakePatientButton()}
            </div>
            <div id="patient-search-filter">
              <span>{'Registration form status:'}</span>
              <DropDownField
                inputName={FILTER}
                placeholder={FILTER_PLACEHOLDER}
                defaultValue={submissionStatus}
                options={(filtersData && filtersData.searchFiltersRefData) || []}
                onChange={(e): void => {
                  submissionStatusRef.current = e.target.value;
                  if (liveSearchInputRef.current && liveSearchInputRef.current.value !== '') {
                    triggerSubmitRef.current && triggerSubmitRef.current.submit();
                  }
                }}
              />
              <a
                href={searchTerm ? `${BASE_SEARCH_URL}?${SEARCH_NAME}=${searchTerm}` : BASE_SEARCH_URL}
                onClick={(): void => {
                  triggerSubmitRef.current && triggerSubmitRef.current.submit();
                }}>
                {'Clear filter'}
              </a>
            </div>
          </form>
          <br />
        </Stack>
        {searchTerm && renderSearchResultsSection()}
      </PageContainer>
    </Fragment>
  );
};

export default UKPatientSearch;
